import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
/* global requestAnimationFrame cancelAnimationFrame */
const SmartDraggableItem = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging
  } = useDraggable({
    id: props.id,
    data: props.data
  })

  const localRef = useRef(null)
  const pointerPosition = useRef({ x: 0, y: 0 })
  const scrollParentRef = useRef(null)
  const [originRect, setOriginRect] = useState(null)
  const [dragOffset, setDragOffset] = useState(null)

  useLayoutEffect(() => {
    if (localRef.current) {
      let parent = localRef.current.parentElement
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent)
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          scrollParentRef.current = parent
          break
        }
        parent = parent.parentElement
      }
    }
  }, [])

  useLayoutEffect(() => {
    if (isDragging && localRef.current) {
      const rect = localRef.current.getBoundingClientRect()
      setOriginRect({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height
      })

      setDragOffset({
        x: pointerPosition.current.x - rect.left,
        y: pointerPosition.current.y - rect.top
      })
    }
  }, [isDragging])

  useEffect(() => {
    function onPointerMove (event) {
      pointerPosition.current = {
        x: event.clientX,
        y: event.clientY
      }
    }

    window.addEventListener('pointermove', onPointerMove)
    return () => window.removeEventListener('pointermove', onPointerMove)
  }, [])

  useEffect(() => {
    if (!isDragging) {
      setDragOffset(null)
      return
    }

    const SCROLL_MARGIN = 100
    const SCROLL_SPEED = 4
    const MAX_SCROLL_SPEED = 10

    let animationFrameId
    let lastTimestamp = 0
    const scrollTarget = scrollParentRef.current || window

    const scrollStep = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp
      const deltaTime = timestamp - lastTimestamp
      lastTimestamp = timestamp

      const scrollAmount = (speed) => speed * (deltaTime / 16)

      const clientHeight =
        scrollTarget === window
          ? window.innerHeight
          : scrollTarget.getBoundingClientRect().height

      const y = pointerPosition.current.y

      if (y > clientHeight - SCROLL_MARGIN) {
        const distance = clientHeight - y
        const factor = 1 - distance / SCROLL_MARGIN
        const speed = Math.min(SCROLL_SPEED + factor * 5, MAX_SCROLL_SPEED)
        scrollTarget.scrollBy?.({ top: scrollAmount(speed), behavior: 'instant' })
      } else if (y < SCROLL_MARGIN) {
        const distance = y
        const factor = 1 - distance / SCROLL_MARGIN
        const speed = Math.min(SCROLL_SPEED + factor * 5, MAX_SCROLL_SPEED)
        scrollTarget.scrollBy?.({ top: -scrollAmount(speed), behavior: 'instant' })
      }

      animationFrameId = requestAnimationFrame(scrollStep)
    }

    animationFrameId = requestAnimationFrame(scrollStep)

    return () => cancelAnimationFrame(animationFrameId)
  }, [isDragging])

  let style = { ...props.customStyle, userSelect: 'none' }

  if (isDragging && dragOffset) {
    style = {
      ...style,
      position: 'fixed',
      top: `${pointerPosition.current.y - dragOffset.y}px`,
      left: `${pointerPosition.current.x - dragOffset.x}px`,
      width: originRect?.width ? `${originRect.width}px` : undefined,
      height: originRect?.height ? `${originRect.height}px` : undefined,
      zIndex: 1100,
      pointerEvents: 'none',
      transform: 'none'
    }
  }

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        localRef.current = node
      }}
      style={style}
      {...listeners}
      {...attributes}
    >
      {props.children}
    </div>
  )
}

export default SmartDraggableItem
